import { useEffect, useState } from 'react';
import { BarChart3, Users, Settings, CreditCard, Loader2, ScrollText, ShieldCheck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { PortfolioHealthViews } from './PortfolioHealthViews';
import { ConsultantAnalyticsDashboard } from './ConsultantAnalyticsDashboard';
import { AuditLog } from './AuditLog';
import { FirmRoleManager } from './FirmRoleManager';
import { FirmBrandingSettings } from './FirmBrandingSettings';
import { FirmScopingSettings } from './FirmScopingSettings';
import { FirmProfileSettings } from './FirmProfileSettings';
import { EngagementCatalog } from './EngagementCatalog';
import { SOWTemplateLibrary } from './SOWTemplateLibrary';
import { ProposalTemplateLibrary } from './ProposalTemplateLibrary';
import { QuestionBankBrowser } from './question-bank/QuestionBankBrowser';
import { QueryProvider } from '../providers/QueryProvider';
import { FirmDefaultTemplateSettings } from './FirmDefaultTemplateSettings';
import { EmailTemplateManager } from './EmailTemplateManager';
import { ConsultantPermissionsManager } from './ConsultantPermissionsManager';
import { BillingAdminDashboard } from './BillingAdminDashboard';
import { FirmLicenses } from './FirmLicenses';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';
import { isBillingAdminUser, isFirmAdminUser } from '../utils/workspaceRouter';
import { toast } from 'sonner';

/**
 * The Firm workspace (SIMPLIFICATION_PLAN.md §1): the tenant itself, in four
 * tabs. Portfolio is the leader view that used to sit on every consultant's
 * landing; Team and Settings are Firm-managed (everyone can look, a Firm
 * Admin changes); Billing is the money tier, shown to Firm Admins and Billing
 * Admins only. Audit is the firm's trail (GET /firms/:firmId/audit).
 *
 * Settings holds ten sections in three groups (the firm, selling, discovery),
 * one on screen at a time; the three D4 restores (default template, email
 * templates, AI context access) live here, and the firm profile (name,
 * subdomain, stats: RESTORE_PLAN R3.1) opens the first group.
 */

export type FirmTab = 'portfolio' | 'team' | 'settings' | 'billing' | 'licenses' | 'audit';

type SettingsSection = 'profile' | 'branding' | 'default-template' | 'scoping' | 'catalog' | 'sow-templates' | 'proposal-templates' | 'question-bank' | 'email-templates' | 'ai-access';

export const SETTINGS_GROUPS: { label: string; sections: { id: SettingsSection; label: string; blurb: string }[] }[] = [
  { label: 'The firm', sections: [
    { id: 'profile', label: 'Firm profile', blurb: 'Name, sign-in subdomain and the numbers' },
    { id: 'branding', label: 'Branding', blurb: 'Logo, colors and the name clients see' },
    { id: 'default-template', label: 'Default template', blurb: 'The platform template a new client starts from' },
    { id: 'scoping', label: 'Scoping defaults', blurb: 'Rates, estimates and the runbook the Scope Guardian checks against' },
  ] },
  { label: 'Selling', sections: [
    { id: 'catalog', label: 'Engagement catalog', blurb: 'The engagement types the firm sells' },
    { id: 'sow-templates', label: 'SOW templates', blurb: 'Starting points for a Statement of Work' },
    { id: 'proposal-templates', label: 'Proposal templates', blurb: 'Starting points for a proposal' },
  ] },
  { label: 'Discovery', sections: [
    { id: 'question-bank', label: 'Question bank', blurb: 'The firm\'s questions on top of the system template' },
    { id: 'email-templates', label: 'Email templates', blurb: 'Invitations and reminders in the firm\'s words' },
    { id: 'ai-access', label: 'AI context access', blurb: 'Which consultant may use which client\'s AI context' },
  ] },
];

interface FirmWorkspaceProps {
  accessToken: string;
  currentUser: any;
  engagements: any[];
  onViewClientDetails: (companyId: string) => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export function firmTabsFor(user: any): FirmTab[] {
  const tabs: FirmTab[] = ['portfolio', 'team', 'settings'];
  if (isFirmAdminUser(user) || isBillingAdminUser(user)) tabs.push('billing');
  // The client license portfolio (the license management add-on) is the firm admin's
  if (isFirmAdminUser(user)) tabs.push('licenses');
  tabs.push('audit');
  return tabs;
}

function FirmBilling({ accessToken, currentUser }: { accessToken: string; currentUser: any }) {
  const [firm, setFirm] = useState<any>(null);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const firmId = currentUser.firmId || currentUser.consultingFirmId;

  const load = async () => {
    if (!firmId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [firmRes, usersRes] = await Promise.all([
        fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consulting-firms/${firmId}`, { headers: getAuthHeaders(accessToken) }),
        fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/users`, { headers: getAuthHeaders(accessToken) }),
      ]);
      if (!firmRes.ok) throw new Error(`Could not load the firm (${firmRes.status})`);
      // GET /consulting-firms/:firmId answers { firm }; the billing cards read firm.id
      const body = await firmRes.json();
      const record = body?.firm ?? body;
      setFirm(record ? { ...record, id: record.id ?? firmId } : null);
      setAllUsers(usersRes.ok ? await usersRes.json() : []);
    } catch (e: any) {
      toast.error(e?.message || 'Could not load billing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [accessToken, firmId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-gray-400" aria-hidden="true" /></div>;
  if (!firm) return <Card><CardContent className="py-8 text-center text-muted-foreground">Billing needs a firm on your profile. Ask your Firm Admin to add you to the firm.</CardContent></Card>;
  return <BillingAdminDashboard currentUser={currentUser} firm={firm} allUsers={allUsers} accessToken={accessToken} onDataChanged={load} />;
}

export function FirmWorkspace({ accessToken, currentUser, engagements, onViewClientDetails, activeTab, onTabChange }: FirmWorkspaceProps) {
  const firmId: string = currentUser.firmId || currentUser.consultingFirmId || '';
  const firmAdmin = isFirmAdminUser(currentUser);
  const tabs = firmTabsFor(currentUser);
  const [section, setSection] = useState<SettingsSection>('profile');

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="space-y-6">
      <TabsList className="flex flex-wrap h-auto p-1 gap-1 justify-start w-full bg-muted/50">
        <TabsTrigger value="portfolio" className="whitespace-nowrap"><BarChart3 className="size-4 mr-2" aria-hidden="true" />Portfolio</TabsTrigger>
        <TabsTrigger value="team" className="whitespace-nowrap"><Users className="size-4 mr-2" aria-hidden="true" />Team</TabsTrigger>
        <TabsTrigger value="settings" className="whitespace-nowrap"><Settings className="size-4 mr-2" aria-hidden="true" />Settings</TabsTrigger>
        {tabs.includes('billing') && (
          <TabsTrigger value="billing" className="whitespace-nowrap"><CreditCard className="size-4 mr-2" aria-hidden="true" />Billing</TabsTrigger>
        )}
        {tabs.includes('licenses') && (
          <TabsTrigger value="licenses" className="whitespace-nowrap"><ShieldCheck className="size-4 mr-2" aria-hidden="true" />Licenses</TabsTrigger>
        )}
        <TabsTrigger value="audit" className="whitespace-nowrap"><ScrollText className="size-4 mr-2" aria-hidden="true" />Audit</TabsTrigger>
      </TabsList>

      {/* Portfolio: the leader view (ledger L36), off the consultant landing */}
      <TabsContent value="portfolio" className="space-y-6">
        <PortfolioHealthViews engagements={engagements} onViewClientDetails={onViewClientDetails} accessToken={accessToken} />
        {/* Status, hours and revenue across the firm's engagements (the consultant's Analytics tab keeps the activity trend) */}
        <ConsultantAnalyticsDashboard accessToken={accessToken} sections="portfolio" />
      </TabsContent>

      {/* Team: people and roles (Firm-managed) */}
      <TabsContent value="team" className="space-y-6">
        {firmAdmin && firmId ? (
          <FirmRoleManager firmId={firmId} accessToken={accessToken} currentUser={currentUser} />
        ) : (
          <Card><CardContent className="py-8 text-center text-muted-foreground">Your Firm Admin adds people, sets their roles and specialization, and assigns clients here.</CardContent></Card>
        )}
      </TabsContent>

      {/* Settings: the firm's defaults (Firm-managed), one section at a time */}
      <TabsContent value="settings">
        {!firmId ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">Settings need a firm on your profile.</CardContent></Card>
        ) : !firmAdmin ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground">Branding, scoping defaults, the engagement catalog, templates and the question bank are set by your Firm Admin. Ask them when something here needs to change.</CardContent></Card>
        ) : (
          <div className="flex flex-col gap-6 md:flex-row">
            <nav aria-label="Settings sections" className="md:w-56 shrink-0 space-y-4">
              {SETTINGS_GROUPS.map((g) => (
                <div key={g.label}>
                  <div className="px-3 pb-1 text-muted-foreground uppercase tracking-wide">{g.label}</div>
                  <ul className="flex flex-row flex-wrap gap-1 md:flex-col">
                    {g.sections.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          aria-current={section === s.id ? 'page' : undefined}
                          onClick={() => setSection(s.id)}
                          className={`w-full rounded-md px-3 py-2 text-left transition-colors ${section === s.id ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:bg-white/60'}`}
                        >
                          <span className="block">{s.label}</span>
                          <span className="hidden md:block text-muted-foreground">{s.blurb}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
            <div className="min-w-0 flex-1 space-y-6">
              {section === 'profile' && <FirmProfileSettings accessToken={accessToken} firmId={firmId} />}
              {section === 'branding' && <FirmBrandingSettings accessToken={accessToken} firmId={firmId} />}
              {section === 'default-template' && <FirmDefaultTemplateSettings accessToken={accessToken} firmId={firmId} />}
              {section === 'scoping' && <FirmScopingSettings accessToken={accessToken} firmId={firmId} />}
              {section === 'catalog' && <EngagementCatalog accessToken={accessToken} firmId={firmId} />}
              {section === 'sow-templates' && <SOWTemplateLibrary accessToken={accessToken} firmId={firmId} onApplyTemplate={() => toast.success('Template applied. Create a new SOW to use it.')} />}
              {section === 'proposal-templates' && <ProposalTemplateLibrary accessToken={accessToken} firmId={firmId} onApplyTemplate={() => toast.success('Proposal template ready.')} />}
              {section === 'question-bank' && <QueryProvider><QuestionBankBrowser firmId={firmId} /></QueryProvider>}
              {section === 'email-templates' && <EmailTemplateManager accessToken={accessToken} firmId={firmId} />}
              {section === 'ai-access' && <ConsultantPermissionsManager firmId={firmId} accessToken={accessToken} projectId={projectId} publicAnonKey={publicAnonKey} />}
            </div>
          </div>
        )}
      </TabsContent>

      {/* Billing: the money tier */}
      {tabs.includes('billing') && (
        <TabsContent value="billing">
          <FirmBilling accessToken={accessToken} currentUser={currentUser} />
        </TabsContent>
      )}

      {/* Licenses: the client license portfolio and renewals (the kept license screens, RESTORE_PLAN R3.3) */}
      {tabs.includes('licenses') && firmId && (
        <TabsContent value="licenses">
          <FirmLicenses accessToken={accessToken} firmId={firmId} currentUser={currentUser} />
        </TabsContent>
      )}

      {/* Audit: every change on the firm's clients or by its members, with a name (ledger L45) */}
      <TabsContent value="audit">
        {firmId ? (
          <AuditLog accessToken={accessToken} firmId={firmId} maxResults={300} />
        ) : (
          <Card><CardContent className="py-8 text-center text-muted-foreground">The audit trail needs a firm on your profile.</CardContent></Card>
        )}
      </TabsContent>
    </Tabs>
  );
}
